import { cjk } from "@streamdown/cjk";
import { code } from "@streamdown/code";
import { memo } from "react";
import type { ReactElement } from "react";
import { Streamdown } from "streamdown";
// Keyframes and the [data-sd-animate] rule backing `animated`. Imported as a
// global sheet rather than through the SCSS module because CSS Modules rewrite
// @keyframes names, which would no longer match the animation names streamdown
// stamps inline.
import "streamdown/styles.css";
import cx from "ui/cx";
import styles from "./MarkdownRenderer.module.scss";

const streamdownPlugins = { cjk, code };
const streamdownControls = { table: { fullscreen: false } };
const streamdownLinkSafety = { enabled: false };

export type MarkdownMode = "static" | "streaming";

export interface MarkdownRendererProps {
  readonly children: string;
  readonly mode?: MarkdownMode;
  readonly className?: string;
}

// Uses `streamdown` (vs. `react-markdown`) because consumers stream tokens
// incrementally and need safe partial-document parsing. Static markdown would
// otherwise be fine to render with `react-markdown`.
//
// Safety: streamdown ships rehype-harden by default, which strips <script>
// tags, on* event handler attributes, and javascript:/dangerous URL schemes.
// Safe for AI-generated and user-supplied markdown. Do not bypass the renderer
// (no dangerouslySetInnerHTML on its output, no custom rehype config that
// removes harden).
export const MarkdownRenderer = memo(function MarkdownRenderer({
  children,
  mode = "static",
  className,
}: MarkdownRendererProps): ReactElement {
  // Drives streamdown's per-word reveal, which paces a burst of tokens into a
  // steady cascade. It has to switch off again on settle, or the wrapper spans
  // it adds around each word stay in the DOM and break anything that walks
  // text nodes, find-in-page included.
  const isStreaming = mode === "streaming";

  return (
    <Streamdown
      animated={isStreaming}
      isAnimating={isStreaming}
      className={cx(styles.markdown, className)}
      // Fullscreen renders the table in a portal on document.body, outside both the
      // panel and the .markdown scope every style in this module hangs off.
      controls={streamdownControls}
      // On by default, which puts a confirmation modal in front of every link.
      // The modal is styled with Tailwind classes this app does not load, and the
      // agent's links are already sanitized by rehype-harden.
      linkSafety={streamdownLinkSafety}
      mode={mode}
      plugins={streamdownPlugins}
    >
      {children}
    </Streamdown>
  );
});

MarkdownRenderer.displayName = "MarkdownRenderer";

export default MarkdownRenderer;
